import { motion } from "framer-motion";
import { ArrowRight, Zap, ShieldCheck } from "lucide-react";
import { HERO_STATS } from "@/data/content";

const Hero = () => (
  <section
    id="home"
    data-testid="hero-section"
    className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden bg-gradient-to-b from-sky-50 via-white to-white"
  >
    <div className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-primary/10 blur-3xl pointer-events-none" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <div className="text-center lg:text-left">
        <motion.span
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-4 py-1.5 text-sm font-semibold"
        >
          <Zap className="w-4 h-4 fill-primary" /> Fast, Reliable &amp; Professional
        </motion.span>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.08] tracking-tight text-foreground"
        >
          Your Delivery. <span className="text-primary">Our Priority.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.12 }}
          className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl mx-auto lg:mx-0"
        >
          A specialized medical delivery service dedicated to the highest standard of professional
          courier services in healthcare. Precision, reliability, and integrity — non-negotiable,
          every single time.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.18 }}
          className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start"
        >
          <a
            href="#contact"
            data-testid="hero-quote-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 text-base font-semibold text-white shadow-xl shadow-primary/25 hover:bg-primary/90 hover:-translate-y-0.5 transition-all"
          >
            Get a Free Quote <ArrowRight className="w-5 h-5" />
          </a>
          <a
            href="#services"
            data-testid="hero-services-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-white border-2 border-border px-7 py-3.5 text-base font-semibold text-foreground hover:border-primary hover:text-primary transition-all"
          >
            Our Services
          </a>
        </motion.div>

        <div className="mt-10 grid grid-cols-3 gap-4 sm:gap-6 max-w-lg mx-auto lg:mx-0">
          {HERO_STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25 + i * 0.08 }}
              className="text-center lg:text-left"
            >
              <div className="text-2xl sm:text-3xl font-extrabold text-foreground">{s.value}</div>
              <div className="text-xs sm:text-sm text-muted-foreground mt-0.5">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, delay: 0.1 }}
        className="relative"
      >
        <div className="relative rounded-3xl bg-white border border-border/60 shadow-2xl shadow-primary/10 p-5 sm:p-8">
          <img
            src="/assets/logo.png"
            alt="On Time Couriers delivery van"
            className="w-full h-auto object-contain"
          />
        </div>
        <div className="absolute -bottom-5 left-4 sm:left-8 flex items-center gap-3 rounded-2xl bg-white shadow-xl border border-border/60 px-4 py-3">
          <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10">
            <ShieldCheck className="w-5 h-5 text-primary" />
          </span>
          <div>
            <div className="text-sm font-bold text-foreground">Fully Insured</div>
            <div className="text-xs text-muted-foreground">Every delivery protected</div>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

export default Hero;
